import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import api from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { getRoleCapabilities } from "../../config/permissions";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  { value: "designer", label: "User (Designer)" },
];

export default function Users() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { user: currentUser } = useAuth();
  const caps = useMemo(() => getRoleCapabilities(currentUser?.role), [currentUser?.role]);

  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    passwordConfirm: "",
    role: "designer",
    assignedBrands: [],
  });
  const [formError, setFormError] = useState("");

  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdRow, setPwdRow] = useState(null);
  const [pwdNew, setPwdNew] = useState("");
  const [pwdConfirm, setPwdConfirm] = useState("");
  const [pwdError, setPwdError] = useState("");

  const [transferOpen, setTransferOpen] = useState(false);
  const [transfer, setTransfer] = useState({
    fromUserId: "",
    toUserId: "",
    brandIds: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, b] = await Promise.all([api.get("/api/users"), api.get("/api/brands")]);
      setRows(
        (u.data || []).map((r) => ({
          id: r.id,
          name: r.name,
          email: r.email,
          role: r.role,
          assignedBrands: r.assignedBrands || [],
          assignedBrandDocs: r.assignedBrandDocs || [],
        }))
      );
      setBrands(b.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (caps.canAccessUsers) load();
  }, [load, caps.canAccessUsers]);

  const openCreate = () => {
    setEditing(null);
    setFormError("");
    setForm({ name: "", email: "", password: "", passwordConfirm: "", role: "designer", assignedBrands: [] });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFormError("");
    setForm({
      name: row.name || "",
      email: row.email || "",
      password: "",
      passwordConfirm: "",
      role: row.role,
      assignedBrands: (row.assignedBrands || []).map(String),
    });
    setOpen(true);
  };

  const openPwd = (row) => {
    setPwdRow(row);
    setPwdNew("");
    setPwdConfirm("");
    setPwdError("");
    setPwdOpen(true);
  };

  const savePwd = async () => {
    setPwdError("");
    if (!pwdRow) return;
    if (pwdNew.length < 6) {
      setPwdError("Password must be at least 6 characters.");
      return;
    }
    if (pwdNew !== pwdConfirm) {
      setPwdError("Passwords do not match.");
      return;
    }
    try {
      await api.patch(`/api/users/${pwdRow.id}`, { password: pwdNew });
      setPwdOpen(false);
      await load();
    } catch (e) {
      setPwdError(e?.response?.data?.message || e?.message || "Could not update password");
    }
  };

  const saveUser = async () => {
    setFormError("");
    if (!editing) {
      if (!form.password || form.password.length < 6) {
        setFormError("Password must be at least 6 characters.");
        return;
      }
      if (form.password !== form.passwordConfirm) {
        setFormError("Password and confirmation do not match.");
        return;
      }
    }
    try {
      if (editing) {
        const payload = { name: form.name, assignedBrands: form.assignedBrands };
        if (caps.canAccessUsers) {
          payload.email = form.email;
          payload.role = form.role;
        }
        await api.patch(`/api/users/${editing.id}`, payload);
      } else {
        await api.post("/api/users", {
          name: form.name,
          email: form.email,
          password: form.password,
          role: form.role,
          assignedBrands: form.assignedBrands,
        });
      }
      setOpen(false);
      await load();
    } catch (e) {
      setFormError(e?.response?.data?.message || e?.message || "Save failed");
    }
  };

  const deleteUser = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    await api.delete(`/api/users/${id}`);
    await load();
  };

  const submitTransfer = async () => {
    await api.post("/api/users/transfer-brands", {
      fromUserId: transfer.fromUserId,
      toUserId: transfer.toUserId,
      brandIds: transfer.brandIds,
    });
    setTransferOpen(false);
    setTransfer({ fromUserId: "", toUserId: "", brandIds: [] });
    await load();
  };

  const toggleBrand = (id) => {
    const s = new Set(form.assignedBrands.map(String));
    const k = String(id);
    if (s.has(k)) s.delete(k);
    else s.add(k);
    setForm({ ...form, assignedBrands: [...s] });
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.2 },
    { field: "role", headerName: "Role", width: 140 },
    {
      field: "brands",
      headerName: "Brands",
      flex: 1,
      valueGetter: (params) =>
        (params.row?.assignedBrandDocs || [])
          .map((d) => d.brandName || d.id)
          .filter(Boolean)
          .join(", "),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 300,
      renderCell: (params) => (
        <Box display="flex" gap={1} flexWrap="wrap">
          {caps.canAccessUsers && (
            <Button size="small" sx={{ color: "#ffffff" }} onClick={() => openEdit(params.row)}>
              Edit
            </Button>
          )}
          {caps.canAccessUsers && (
            <Button size="small" sx={{ color: "#ffffff" }} onClick={() => openPwd(params.row)}>
              Password
            </Button>
          )}
          {caps.canAccessUsers && params.row.id !== currentUser?.id && (
            <Button size="small" color="error" onClick={() => deleteUser(params.row.id)}>
              Delete
            </Button>
          )}
        </Box>
      ),
    },
  ];

  if (!caps.canAccessUsers) {
    return (
      <Box m="20px">
        <Header title="USERS" subtitle="You do not have access to user management." />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="USERS" subtitle="Manage accounts, roles, and brand access" />
        <Box display="flex" gap={1}>
          {caps.canTransferBrandsBetweenDesigners && (
            <Button variant="outlined" color="secondary" onClick={() => setTransferOpen(true)}>
              Transfer brands
            </Button>
          )}
          {caps.canAccessUsers && (
            <Button variant="contained" color="secondary" onClick={openCreate}>
              Add User
            </Button>
          )}
        </Box>
      </Box>

      <Box height="70vh" sx={gridSx(colors)}>
        <DataGrid loading={loading} rows={rows} columns={columns} checkboxSelection={false} />
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle>
        <DialogContent>
          {formError ? (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {formError}
            </Typography>
          ) : null}
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Email"
            fullWidth
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            disabled={!!editing && currentUser?.role !== "admin"}
          />
          {!editing ? (
            <>
              <TextField
                margin="dense"
                label="Password"
                type="password"
                fullWidth
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <TextField
                margin="dense"
                label="Confirm password"
                type="password"
                fullWidth
                value={form.passwordConfirm}
                onChange={(e) => setForm({ ...form, passwordConfirm: e.target.value })}
              />
            </>
          ) : null}
          {currentUser?.role === "admin" && (
            <FormControl fullWidth margin="dense">
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
              >
                {ROLES.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {editing && currentUser?.role !== "admin" ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              Role: <strong>{form.role}</strong> (only an admin can change roles)
            </Typography>
          ) : null}
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Brand access
          </Typography>
          <FormGroup>
            {brands.map((b) => (
              <FormControlLabel
                key={b.id}
                control={
                  <Checkbox
                    checked={form.assignedBrands.map(String).includes(String(b.id))}
                    onChange={() => toggleBrand(b.id)}
                  />
                }
                label={b.brandName || b.id}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={saveUser}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={pwdOpen} onClose={() => setPwdOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Set password for {pwdRow?.email || "user"}</DialogTitle>
        <DialogContent>
          {pwdError ? (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>
              {pwdError}
            </Typography>
          ) : null}
          <TextField
            margin="dense"
            label="New password"
            type="password"
            fullWidth
            value={pwdNew}
            onChange={(e) => setPwdNew(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Confirm new password"
            type="password"
            fullWidth
            value={pwdConfirm}
            onChange={(e) => setPwdConfirm(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwdOpen(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={savePwd}>
            Save password
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Transfer brands between users</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>From user</InputLabel>
            <Select
              label="From user"
              value={transfer.fromUserId}
              onChange={(e) => setTransfer({ ...transfer, fromUserId: e.target.value })}
            >
              {rows.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.email} ({r.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>To user</InputLabel>
            <Select
              label="To user"
              value={transfer.toUserId}
              onChange={(e) => setTransfer({ ...transfer, toUserId: e.target.value })}
            >
              {rows.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.email} ({r.role})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Brands to move
          </Typography>
          <FormGroup>
            {brands.map((b) => (
              <FormControlLabel
                key={b.id}
                control={
                  <Checkbox
                    checked={transfer.brandIds.map(String).includes(String(b.id))}
                    onChange={() => {
                      const s = new Set(transfer.brandIds.map(String));
                      const k = String(b.id);
                      if (s.has(k)) s.delete(k);
                      else s.add(k);
                      setTransfer({ ...transfer, brandIds: [...s] });
                    }}
                  />
                }
                label={b.brandName || b.id}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={submitTransfer}>
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function gridSx(colors) {
  return {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
  };
}
